import { ReactNode, useState } from 'react'
import { AccessTime } from '@mui/icons-material'
import {
  Alert,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Trans, useTranslation } from 'react-i18next'
import {
  Form,
  TwoFactorCodeField,
  TwoFactorCodeFormFields,
  useTwoFactorCodeForm,
} from 'shared/components'
import { MODULE_NAME } from '../../../strings'
import {
  SectionTitle,
  StepActions,
  StepContent,
  TextActionLink,
} from '../ReauthenticationDialog.style'
import SupportInfoStep from './SupportInfoStep'

interface CodeStepProps {
  email: string
  notices: ReactNode
  error: string | null
  isBusy: boolean
  onSubmit: (code: string) => Promise<void>
  onBack: () => void
}

// Going back unmounts the step, which clears the code
const CodeStep = ({
  email,
  notices,
  error,
  isBusy,
  onSubmit,
  onBack,
}: CodeStepProps) => {
  const { t } = useTranslation(MODULE_NAME)
  const { t: tCommon } = useTranslation()
  const { formProps, formError } = useTwoFactorCodeForm(onSubmit, error)
  const [isSupportInfoVisible, setIsSupportInfoVisible] = useState(false)

  if (isSupportInfoVisible)
    return <SupportInfoStep onBack={() => setIsSupportInfoVisible(false)} />

  return (
    <Form {...formProps}>
      <StepContent>
        <Stack spacing={2.5}>
          <DialogContentText variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="twoFactor.descriptionWithEmail"
              values={{ email }}
              components={[<strong key={0} />]}
            />
          </DialogContentText>
          {notices}
          {formError && <Alert severity="error">{formError}</Alert>}
          <div>
            <SectionTitle variant="h3">{t('twoFactor.code')}</SectionTitle>
            <TwoFactorCodeField
              required
              name={TwoFactorCodeFormFields.Code}
              label={t('twoFactor.code')}
              disabled={isBusy}
            />
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ mt: 1.75, color: 'grey.600' }}
            >
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontSize={13}>
                {tCommon('reauthentication.codeRefreshHint')}
              </Typography>
            </Stack>
          </div>
          <TextActionLink
            component="button"
            type="button"
            variant="body2"
            underline="none"
            onClick={() => setIsSupportInfoVisible(true)}
          >
            {t('twoFactor.noAccessToApp')}
          </TextActionLink>
        </Stack>
      </StepContent>
      <StepActions disableSpacing>
        <Button variant="outlined" onClick={onBack} disabled={isBusy}>
          {t('login.goBack')}
        </Button>
        <LoadingButton type="submit" variant="contained" loading={isBusy}>
          {t('twoFactor.verify')}
        </LoadingButton>
      </StepActions>
    </Form>
  )
}

export default CodeStep
